'use client'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { ArrowUp } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

interface Feature {
  icon: string
  title: string
  subtitle?: string
  description?: string
}

interface Props {
  features: Feature[]
  rawText?: boolean
}

export const CategoryFeatureDesktop = ({
  features,
  rawText = false,
}: Props) => {
  const t = useTranslations()
  const getText = (text: string) => (rawText ? text : t(text))

  return (
    <div
      className={cn(
        'grid auto-rows-fr grid-cols-1',
        features.length < 4
          ? 'md:flex md:justify-center md:gap-x-8'
          : 'md:grid-cols-4'
      )}
    >
      {features.map((feature, index) => (
        <div
          key={index}
          className={cn(
            'group relative cursor-pointer px-6 py-8 transition-all duration-500 ease-in-out',
            index < features.length - 1 && features.length >= 4
              ? 'border-r border-gray-600'
              : '',
            features.length < 4 ? 'md:w-1/4' : ''
          )}
        >
          <div className='relative h-full'>
            {/* Default state (visible when not hovered) */}
            <div className='flex h-full flex-col justify-between opacity-100 transition-all duration-500 ease-in-out group-hover:opacity-0'>
              <div className='flex w-full items-center justify-center'>
                <Image
                  src={ImageHelper.getImageUrlCdn(feature.icon)}
                  alt={getText(feature.title)}
                  width={180}
                  height={180}
                  className='h-28 w-28 2xl:h-32 2xl:w-32'
                />
              </div>
              <div
                className={cn(
                  'mt-20 flex w-full items-center justify-between',
                  features.length < 4 ? 'gap-4' : ''
                )}
              >
                <div className='text-white'>
                  <h3 className='mb-1 text-xl font-medium md:text-2xl'>
                    {getText(feature.title)}
                  </h3>
                  {feature.subtitle ? (
                    <p className='text-sm text-gray-300 md:text-lg'>
                      {getText(feature.subtitle)}
                    </p>
                  ) : null}
                </div>
                <button className='flex h-12 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-black 2xl:h-13 2xl:w-12'>
                  <ArrowUp className='h-6 w-6 2xl:h-8 2xl:w-8' />
                </button>
              </div>
            </div>
            {/* Hover state (visible when hovered) with slide-up animation */}
            <div className='absolute inset-0 translate-y-4 px-4 opacity-0 transition-all duration-500 ease-in-out group-hover:translate-y-0 group-hover:opacity-100'>
              <div>
                <h3 className='mb-2 text-2xl font-semibold text-white'>
                  {getText(feature.title)}
                </h3>
                {feature.subtitle ? (
                  <p className='text-sm text-gray-300 md:text-lg'>
                    {getText(feature.subtitle)}
                  </p>
                ) : null}
                <p className='mt-2 border-t pt-2 text-sm leading-relaxed text-gray-300 md:text-lg'>
                  {feature?.description ? getText(feature?.description) : ''}
                </p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
